import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import BridgeText from "./BridgeText";
import { cn } from "../utils/classNames";
import { handleInternalLinkClick } from "../utils/navigation";
import { bridgePreviewText, cleanBridgeText } from "../utils/bridgeText";

export default function RecommendationRibbon({
  nextWatch,
  anchorTitle = "",
  compact = false,
  overlay = false,
  className,
  popoverClassName,
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const anchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const bridgeId = useId();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closeSoon = () => {
    if (pinned) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (anchorRef.current?.contains(event.target)) {
        return;
      }
      setPinned(false);
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPinned(false);
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!nextWatch?.slug || !nextWatch?.title) return null;
  const bridgeText = typeof nextWatch.bridge === "string" ? nextWatch.bridge.trim() : "";
  const bridgePreview = bridgeText ? bridgePreviewText(bridgeText, anchorTitle, nextWatch.title) : "";
  const accessibleBridge = bridgeText ? cleanBridgeText(bridgeText) : "";

  return (
    <div
      ref={anchorRef}
      className={cn(overlay ? "absolute" : "relative", "min-w-0", !compact && "h-10", className)}
      onMouseEnter={() => {
        clearCloseTimer();
        setOpen(true);
      }}
      onMouseLeave={closeSoon}
      onFocus={() => setOpen(true)}
    >
      <button
        type="button"
        className={cn(
          "sp-overlay-ribbon",
          compact ? "px-2.5 py-1.5" : "px-3 py-2"
        )}
        aria-expanded={open}
        aria-describedby={accessibleBridge ? bridgeId : undefined}
        data-recommendation-title={nextWatch.title}
        data-recommendation-bridge={nextWatch.bridge || ""}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setPinned((current) => {
            const nextPinned = !current;
            setOpen(nextPinned);
            return nextPinned;
          });
        }}
      >
        <span
          className={cn(
            "sp-overlay-ribbon__muted shrink-0 text-overlay-ribbon-label",
            compact && "text-overlay-ribbon-label-compact"
          )}
        >
          Try next
        </span>
        <ArrowRight className="sp-overlay-ribbon__muted h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="truncate text-overlay-ribbon-title">
          {nextWatch.title}
        </span>
      </button>

      {accessibleBridge && bridgeId && (
        <span id={bridgeId} className="sr-only">
          Try next: {nextWatch.title}. {accessibleBridge}
        </span>
      )}

      <AnimatePresence>
        {open && bridgePreview && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.16, ease: [0.0, 0.0, 0.2, 1] }}
            className={cn("sp-recommendation-popover", popoverClassName)}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={closeSoon}
          >
            <div className="sp-recommendation-popover__eyebrow">
              Why it fits
            </div>
            <p className="sp-recommendation-popover__body">
              <BridgeText text={bridgePreview} titles={[nextWatch.title]} />
            </p>
            <a
              href={`/show/${nextWatch.slug}`}
              onClick={(event) => handleInternalLinkClick(event, `/show/${nextWatch.slug}`)}
              className="mt-2 inline-flex items-center gap-1 text-body-xs font-semibold text-primary hover:underline"
            >
              Open {nextWatch.title}
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

RecommendationRibbon.propTypes = {
  nextWatch: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    bridge: PropTypes.string,
  }),
  anchorTitle: PropTypes.string,
  compact: PropTypes.bool,
  overlay: PropTypes.bool,
  className: PropTypes.string,
  popoverClassName: PropTypes.string,
};
